
import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../redux/initialStoreState";
import { useCustomerPolicyDetails, useDispatchWrapper, useQueryParms } from "../../../../../hooks";
import { PageLoader } from "../../../../../components/PageLoader/PageLoader";
import { LoadState } from "../../../../../constants/enums";
import { StandadCard } from "../../../../../components/Cards";
import { TabsList } from "../../../../../components/Tabs/TabsList";
import { AccountCircle, AdminPanelSettings, ChecklistOutlined } from "@mui/icons-material";
import { TabPanel } from "../../../../../components/Tabs/TabPanel";
import { useNavigate, useOutletContext } from "react-router-dom";
import { CommAutomobileMarketingList } from "./CommAutoMarketing/CommAutoMarketingList";
import { CommAutoBinderList } from "./CommAutoBinder/CommAutoBinderList";
import { CommAutoChecklistTab } from "./CommAutoChecklist/CommAutoChecklistTab";
import { fetchCommAutoChecklistSavedStatusAsync } from "../../../../../redux/commAutoPolicy/commAutoMarketingHistory/commAutoMarketingHistoryActions";
import { PageContainer } from "../../../../../components/container/PageContainer";
import { useCommAutoScope } from "../../../../leads/ManageLead/LeadCommAutoScopeContext";

/**
 * Feature: lead-commercial-auto-risk-profile
 *
 * The three Marketing sub-tabs. Kept as a single ordered source of truth so the
 * sub-tab set is identical in both the customer-policy and lead scopes
 * (Requirements 3.5, 9 — no sub-tab added/removed/renamed/reordered). The
 * Binder sub-tab (index 2) is disabled until the checklist saved status is true
 * in both scopes (Requirement 12.4/12.5, Property 10).
 */
const MARKETING_TABS = (checklistSaved: boolean) => [
  {
    label: "Marketing",
    icon: <AccountCircle />,
  },
  {
    label: "Checklist",
    icon: <ChecklistOutlined />,
  },
  {
    label: "Binder",
    icon: <AdminPanelSettings />,
    disabled: !checklistSaved,
  },
];

/**
 * Feature: lead-commercial-auto-risk-profile
 *
 * Optional lead-scope props. All are additive and optional so the
 * customer-policy route rendering (`useOutletContext` + `?tab=` + `navigate`)
 * is unchanged when none are supplied (Requirements 16.1, 16.2).
 *
 * - `selectedTab` / `onTabChange`: when BOTH are provided the inner `TabsList`
 *   is controlled by local state (lead scope) and `navigate` is never called.
 *   When absent the root keeps reading `?tab=` and calling `navigate(...)`
 *   exactly as before (Requirement 4.4).
 */
interface ICommAutoMarketingRootProps {
  selectedTab?: number;
  onTabChange?: (newValue: number) => void;
}

/**
 * Safe wrapper around `useOutletContext` that returns `undefined` instead of
 * throwing when the root is not rendered under a React Router `<Outlet>` (which
 * is the case inside the Lead Comm Auto panel, since that panel is not a
 * route). In the customer-policy context the outlet tuple is
 * `[customerId, customerPolicyId]`.
 */
const useOutletContextSafe = (): unknown[] | undefined => {
  try {
    return useOutletContext<unknown[]>();
  } catch {
    return undefined;
  }
};

export const CommAutoMarketingRoot: React.FC<ICommAutoMarketingRootProps> = (
  props
) => {
  const { selectedTab: controlledTab, onTabChange } = props;

  // Keep reading the router outlet in the customer-policy context; in lead
  // scope this returns `undefined` (the panel is not a route) and the resolved
  // scope comes from the LeadCommAutoScopeProvider instead.
  const outlet = useOutletContextSafe();
  const outletCustomerId = outlet?.[0] as number | undefined;
  const outletCustomerPolicyId = outlet?.[1] as number | undefined;

  // Lead scope wins; else the route fallback verbatim (Requirements 5.2, 5.3).
  const { customerId, customerPolicyId, leadId } = useCommAutoScope({
    customerId: outletCustomerId,
    customerPolicyId: outletCustomerPolicyId,
  });

  const { details } = useCustomerPolicyDetails(customerPolicyId as number);
  const checklistSaved = useSelector(
    (storeState: IStoreState) =>
      storeState.commAuto.marketing.checklist.savedStatus
  );

  const { getParm } = useQueryParms();
  const tabValue = getParm("tab");

  // Controlled selection is used only when the caller supplies both props
  // (lead scope). Otherwise selection is driven by the `?tab=` query param
  // exactly as before (Requirement 16.2).
  const isControlled = controlledTab != null && onTabChange != null;
  const requestedTab = isControlled
    ? controlledTab
    : tabValue
      ? Number(tabValue)
      : 0;

  // Binder-disabled guard (Requirement 12.4/12.5, Property 10): in lead scope
  // (controlled selection) if the Binder sub-tab (index 2) is requested while
  // the checklist is not saved, clamp the effective selection to the Checklist
  // sub-tab (index 1) so Binder content never renders. This replaces the
  // customer-policy `navigate(...?tab=1)` redirect with a pure selection guard
  // that triggers no browser navigation in lead scope (Requirement 4.4).
  //
  // In the customer-policy scope `selectedTab` is left exactly as before (the
  // `?tab=` value) and the historical `navigate(...?tab=1)` redirect effect
  // below keeps the URL in sync, preserving byte-for-byte behavior.
  const selectedTab =
    isControlled && requestedTab === 2 && !checklistSaved ? 1 : requestedTab;

  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (isControlled) {
      onTabChange(newValue);
      return;
    }
    navigate(
      `/comm-automobile-policy/${customerId}/marketing-tab/${customerPolicyId}?tab=${newValue}`
    );
  };

  const handleChecklistSaved = () => {};

  React.useEffect(() => {
    if (customerPolicyId || leadId) {
      dispatch(
        fetchCommAutoChecklistSavedStatusAsync(
          customerPolicyId as number,
          undefined,
          leadId
        )
      );
    }
  }, [customerPolicyId, leadId]);

  // In the customer-policy scope, preserve the historical behavior of pushing a
  // `?tab=1` redirect when the Binder tab is selected while the checklist is
  // unsaved so the URL stays in sync. In lead scope (controlled selection) the
  // pure `selectedTab` clamp above already prevents Binder from rendering and
  // no navigation is issued (Requirement 4.4).
  React.useEffect(() => {
    if (isControlled) {
      return;
    }
    if (requestedTab === 2 && !checklistSaved) {
      navigate(
        `/comm-automobile-policy/${customerId}/marketing-tab/${customerPolicyId}?tab=1`
      );
    }
  }, [requestedTab, checklistSaved]);

  return (
    <PageContainer title={"Marketing"} description={"Marketing"}>
      <PageLoader
        loading={details.loading === LoadState.InProgress}
        error={details.error ? { message: details.error } : null}
      >
        <StandadCard>
          <TabsList
            sx={{ mb: 1 }}
            varaint="fullWidth"
            selectedTab={selectedTab}
            tabs={MARKETING_TABS(checklistSaved)}
            onChange={handleChange}
          />
          <TabPanel value={selectedTab} index={0}>
            <CommAutomobileMarketingList leadId={leadId} />
          </TabPanel>
          <TabPanel value={selectedTab} index={1}>
            <CommAutoChecklistTab
              customerId={customerId as number}
              customerPolicyId={customerPolicyId as number}
              onSaveSuccess={handleChecklistSaved}
              leadId={leadId}
            />
          </TabPanel>
          <TabPanel value={selectedTab} index={2}>
            <CommAutoBinderList checklistSaved={checklistSaved} leadId={leadId} />
          </TabPanel>
        </StandadCard>
      </PageLoader>
    </PageContainer>
  );
};
